/**
 * Header titles keyed by pathname — aligned with sidebar labels and i18n ids.
 * Used when Redux pageTitle does not apply to the current URL.
 */
export interface RouteHeaderTitle {
  id: string
  defaultMessage: string
}

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname
}

/** Longest prefix wins; list is pre-sorted by descending prefix length */
const PREFIX_TITLES: { prefix: string; title: RouteHeaderTitle }[] = [
  {
    prefix: '/trainingmatirx/usercertificates/',
    title: {
      id: 'title.editUserCourses',
      defaultMessage: 'Edit User Courses',
    },
  },
  {
    prefix: '/trainingmatirx/policies/edit',
    title: {
      id: 'title.editUserPolicyCompliance',
      defaultMessage: 'Edit User Policy Compliance',
    },
  },
  {
    prefix: '/form/templates/anonymous',
    title: {
      id: 'title.form-templates-anonymous',
      defaultMessage: 'Anonymous Form Templates',
    },
  },
  {
    prefix: '/form/templates',
    title: { id: 'menu.main.forms.templates', defaultMessage: 'Blank Forms' },
  },
  {
    prefix: '/reports/live',
    title: { id: 'title.live-reporting', defaultMessage: 'Reporting / Live' },
  },
  { prefix: '/dynamic', title: { id: 'dynamicForm.pageTitle', defaultMessage: 'Dynamic Form' } },
  {
    prefix: '/settings/user',
    title: { id: 'title.settings.user-profile', defaultMessage: 'User Profile' },
  },
].sort((a, b) => b.prefix.length - a.prefix.length)

const EXACT_TITLES: Record<string, RouteHeaderTitle> = {
  '/': { id: 'menu.main.dashboard', defaultMessage: 'Dashboard' },
  '/calendar': { id: 'menu.main.calendar', defaultMessage: 'Calendar' },
  '/journeyManagement': { id: 'menu.main.journey', defaultMessage: 'Journey Management' },
  '/ScheduledItems': { id: 'menu.main.ScheduledItems', defaultMessage: 'Events' },
  '/library/documents': { id: 'menu.main.Documents', defaultMessage: 'Documents' },
  '/library/cultureGroups': {
    id: 'title.culture-groups',
    defaultMessage: 'Document Library / Culture Groups',
  },
  '/trainingmatirx/usercertificates': {
    id: 'menu.main.Course',
    defaultMessage: 'Course Enrollment list',
  },
  '/trainingmatirx/policies': { id: 'menu.main.policies', defaultMessage: 'User Policies' },
  '/trainingmatirx/companypolicies': { id: 'menu.main.companyPolicies', defaultMessage: 'User Policies Management' },
  '/trainingmatirx/Privatecourses': {
    id: 'menu.main.privatecoures',
    defaultMessage: 'LMS Courses',
  },
   '/trainingmatirx/courses': { id: 'menu.main.courses', defaultMessage: 'Courses' },
  '/trainingmatrix/configuration': { id: 'trainingmatrix.config.title', defaultMessage: 'Training Matrix Configuration' },
  '/reports/participation': {
    id: 'reports.participation.title',
    defaultMessage: 'Participation Dashboard',
  },
  '/assets/maintenance': { id: 'menu.main.maintenance', defaultMessage: 'Asset Maintenance' },
  '/assignments/assignedToYou': {
    id: 'menu.main.assignments.you',
    defaultMessage: 'Assigned To Me',
  },
  '/assignments/assignedByYou': {
    id: 'menu.main.assignments.other',
    defaultMessage: 'Assigned To Others',
  },
  '/assignment/recurring': {
    id: 'menu.main.assignment.recurring',
    defaultMessage: 'Recurring Assignment',
  },
  '/forms/completed': { id: 'title.completed-forms', defaultMessage: 'Completed Forms' },
  '/settings/company': {
    id: 'title.settings.company',
    defaultMessage: 'Settings / Company',
  },
  '/locationTracking/picklist': {
    id: 'title.settings.picklists',
    defaultMessage: 'Settings / Picklists',
  },
  '/UsersList': { id: 'menu.main.settings.users', defaultMessage: 'Users' },
  '/communication/emailTemplates': {
    id: 'menu.main.settings.EmailTemplate',
    defaultMessage: 'Email Templates',
  },
  '/communication/companyMessages': {
    id: 'menu.main.settings.CompanyMessages',
    defaultMessage: 'Company Messages',
  },
  '/communication/emailLog': {
    id: 'menu.main.setting.EmailLog',
    defaultMessage: 'Email Log',
  },
  '/communication/smsLog': {
    id: 'menu.main.settings.SMSLog',
    defaultMessage: 'SMS Log',
  },
  '/communication/directMessage': {
    id: 'menu.main.setting.directMessage',
    defaultMessage: 'Direct Message',
  },
  '/settings/counters': { id: 'title.settings.counters', defaultMessage: 'Counters' },
  '/setting/globalsettings': {
    id: 'menu.main.setting.globalSettings',
    defaultMessage: 'Global Settings',
  },
  '/setting/camerasettings': {
    id: 'title.settings.camera',
    defaultMessage: 'Camera Settings',
  },
  '/settings/assignmentStatus': {
    id: 'menu.main.settings.AssignmentStatus',
    defaultMessage: 'Assignment Status',
  },
  '/locationTracking/tracker': {
    id: 'menu.main.location.tracker',
    defaultMessage: 'Tracker',
  },
  '/locationTracking/raster': {
    id: 'menu.main.location.rasters',
    defaultMessage: 'Rasters',
  },
  '/locationTracking/shapeFiles': {
    id: 'menu.main.location.shapeFiles',
    defaultMessage: 'Shape Files',
  },
  '/locationTracking/UserGeneratedShapes': {
    id: 'menu.main.location.userGeneratedShapes',
    defaultMessage: 'User Generated Shapes',
  },
  '/locationTracking': {
    id: 'menu.main.location.tracking.map',
    defaultMessage: 'Location Tracking',
  },
  '/organization': { id: 'title.organization', defaultMessage: 'Team Organization' },
  '/profile': { id: 'title.settings.user-profile-me', defaultMessage: 'My Profile' },
  '/securityModules': {
    id: 'menu.main.settings.security',
    defaultMessage: 'Security',
  },
  '/assets/sitesLocations': {
    id: 'menu.main.sites.locations.you',
    defaultMessage: 'Sites & Locations',
  },
  '/assets/picklists': { id: 'menu.main.picklists', defaultMessage: 'Asset Configuration' },
  '/assets/maintenanceSchedules': {
    id: 'menu.main.maintenance.schedules',
    defaultMessage: 'Maintenance Schedules',
  },
  '/assets/assetsmanagement': { id: 'menu.main.assets', defaultMessage: 'Assets List' },
  '/Forbidden': { id: 'title.forbidden', defaultMessage: 'Forbidden' },
}

export function lookupRouteHeaderTitle(pathname: string): RouteHeaderTitle | undefined {
  const path = normalizePath(pathname)
  const exact = EXACT_TITLES[path]
  if (exact) {
    return exact
  }
  for (const { prefix, title } of PREFIX_TITLES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return title
    }
  }
  return undefined
}
